import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Eye, EyeOff, Anchor } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_camp-cert-tracker/artifacts/2283n1sz_rr%20logo.png";
const BG_IMAGE = "https://images.unsplash.com/photo-1549035137-44e3408fdcce?w=1920&q=80";

export default function LoginPage() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  
  const { login, token, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Redirect if already logged in
  if (token && user) {
    const defaultPath = user.role === "medical" ? "/medical/dashboard" : "/dashboard";
    const from = location.state?.from?.pathname || defaultPath;
    navigate(from, { replace: true });
    return null;
  } else if (token) {
    // Token exists but user not loaded yet — wait
    return null;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    const success = await login(username, password);
    
    if (success) {
      // login() sets user, but we need the role to decide redirect
      // The user state is set inside login(), so read from response
      try {
        const meRes = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
        });
        const role = meRes.data?.role;
        const defaultPath = role === "medical" ? "/medical/dashboard" : "/dashboard";
        const from = location.state?.from?.pathname || defaultPath;
        navigate(from, { replace: true });
      } catch {
        navigate("/dashboard", { replace: true });
      }
    }
    
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Side - Login Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-md space-y-8">
          {/* Logo and Header */}
          <div className="text-center">
            <img 
              src={LOGO_URL} 
              alt="Rolling River Day Camp" 
              className="h-24 w-24 mx-auto mb-6"
            />
            <h1 className="font-heading text-3xl font-bold text-brand-navy tracking-tight">
              Staff Certification Tracker
            </h1>
            <p className="mt-2 text-slate-500 font-body">
              Sign in to manage staff certifications
            </p>
          </div>

          {/* Login Form */}
          <form onSubmit={handleSubmit} className="space-y-6 mt-8">
            <div className="space-y-2">
              <Label htmlFor="username" className="text-slate-700 font-medium">
                Username
              </Label>
              <Input
                id="username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Enter your username"
                required
                className="h-12 border-slate-200 focus:ring-brand-navy focus:border-brand-navy"
                data-testid="login-username-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-slate-700 font-medium">
                Password
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  required
                  className="h-12 pr-12 border-slate-200 focus:ring-brand-navy focus:border-brand-navy"
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  data-testid="toggle-password-visibility"
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5" />
                  ) : (
                    <Eye className="h-5 w-5" />
                  )}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-brand-navy hover:bg-brand-navy/90 text-white font-semibold rounded-md btn-lift"
              data-testid="login-submit-button"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></span>
                  Signing in...
                </span>
              ) : (
                "Sign In"
              )}
            </Button>
          </form>

          {/* Footer */}
          <p className="text-center text-xs text-slate-400 mt-8">
            Rolling River Day Camp EST. 1992
          </p>
        </div>
      </div>

      {/* Right Side - Background Image */}
      <div 
        className="hidden lg:flex flex-1 relative"
        style={{
          backgroundImage: `url(${BG_IMAGE})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        {/* Overlay */}
        <div className="absolute inset-0 bg-brand-navy/20" />
        
        {/* Decorative Element */}
        <div className="absolute bottom-8 left-8 right-8 bg-white/90 backdrop-blur-sm rounded-lg p-6">
          <div className="flex items-center gap-4">
            <div className="h-12 w-12 rounded-full bg-brand-navy/10 flex items-center justify-center">
              <Anchor className="h-6 w-6 text-brand-navy" strokeWidth={2} />
            </div>
            <div>
              <h3 className="font-heading font-semibold text-brand-navy">
                Certification Management Made Easy
              </h3>
              <p className="text-sm text-slate-600">
                Track CPR, lifeguard, and safety certifications for all staff members
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
